import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";

import { auth, db } from "../../firebase";

const functions = getFunctions(undefined, "us-central1");
const reviewReportActionFn = httpsCallable(functions, "reviewReportAction");
const blockUserFn = httpsCallable(functions, "blockUser");
const getReportBlockStatusFn = httpsCallable(functions, "getReportBlockStatus");

// Phase N8D: reportUser now creates new reports with status
// "pending_admin_review" (see functions/moderation/reports.js) - "pending"/
// "open" are kept here too so any report created before this phase remains
// visible in the active queue.
const ACTIVE_STATUSES = ["pending", "open", "pending_admin_review", "under_review"];

function normalize(s) {
  return String(s ?? "").trim().toLowerCase();
}

function formatCreatedAt(value) {
  if (!value) return "—";
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

function shorten(str, start = 12, end = 8) {
  const s = String(str || "");
  if (s.length <= start + end + 3) return s || "—";
  return `${s.slice(0, start)}…${s.slice(-end)}`;
}

function createdAtMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") {
    try {
      return value.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof value === "object" && value.seconds != null) {
    return Number(value.seconds) * 1000;
  }
  if (typeof value === "number") return value;
  return 0;
}

function badgeClass(status) {
  const s = normalize(status);
  if (s === "pending" || s === "pending_admin_review") {
    return "admin1-reports__badge admin1-reports__badge--pending";
  }
  if (s === "open") return "admin1-reports__badge admin1-reports__badge--open";
  if (s === "under_review") {
    return "admin1-reports__badge admin1-reports__badge--review";
  }
  return "admin1-reports__badge admin1-reports__badge--muted";
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [feedbackById, setFeedbackById] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [contextFilter, setContextFilter] = useState("all");
  // Phase N8D: on-demand, per-report block-status lookup - never
  // auto-fetched for every visible report, only when the admin explicitly
  // asks. "loading" | "error" | { blocked, reporterBlockedReported }.
  const [blockStatusById, setBlockStatusById] = useState({});

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "reports"),
      where("status", "in", ACTIVE_STATUSES)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        list.sort(
          (a, b) =>
            createdAtMs(b.createdAt ?? b.createdAtMs) -
            createdAtMs(a.createdAt ?? a.createdAtMs)
        );
        setReports(list);
        setLoading(false);
      },
      (err) => {
        console.error("ReportsPage snapshot error:", err);
        setReports([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      if (statusFilter !== "all") {
        if (normalize(r.status) !== normalize(statusFilter)) return false;
      }
      if (contextFilter !== "all") {
        const ct = r.contextType == null ? "" : String(r.contextType).trim();
        if (contextFilter === "__empty__") {
          if (ct !== "") return false;
        } else if (normalize(ct) !== normalize(contextFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [reports, statusFilter, contextFilter]);

  async function markUnderReview(report) {
    const id = report.id;
    setWorkingId(id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      await reviewReportActionFn({ reportId: id, action: "under_review" });
    } catch (error) {
      console.error("markUnderReview:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  async function closeReport(report) {
    const id = report.id;
    setWorkingId(id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      await reviewReportActionFn({ reportId: id, action: "closed" });
    } catch (error) {
      console.error("closeReport:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  async function banReportedUser(report) {
    const reportedUid = String(report.reportedUid || "").trim();
    const adminUid = auth.currentUser?.uid;
    if (!reportedUid || !adminUid || reportedUid === adminUid) return;

    const id = report.id;
    setWorkingId(id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    try {
      await blockUserFn({ blockedUid: reportedUid });
    } catch (error) {
      console.error("blockUser:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  // Phase N8D: read-only, admin-gated - never mutates anything, only asks
  // the server (via getReportBlockStatus, which itself only calls the
  // existing blocks.js getBlockState helper) whether the block the client
  // attempted at report time actually took effect.
  async function checkBlockStatus(report) {
    const id = report.id;
    const reporterUid = String(report.reporterUid || "").trim();
    const reportedUid = String(report.reportedUid || "").trim();
    if (!reporterUid || !reportedUid) return;

    setBlockStatusById((prev) => ({ ...prev, [id]: "loading" }));
    try {
      const res = await getReportBlockStatusFn({ reporterUid, reportedUid });
      setBlockStatusById((prev) => ({ ...prev, [id]: res.data }));
    } catch (error) {
      console.error("getReportBlockStatus:", error);
      setBlockStatusById((prev) => ({ ...prev, [id]: "error" }));
    }
  }

  const busy = workingId != null;
  const adminUid = auth.currentUser?.uid;

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.reports.title", { defaultValue: "Reports" })}</h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.reports.subtitle_before_statuses", { defaultValue: "Abuse reports in" })}{" "}
          <span className="admin1-users__kbd">
            pending | open | under_review
          </span>
          . {t("admin.reports.subtitle_status_updates", { defaultValue: "Status updates write only to" })}{" "}
          <span className="admin1-users__kbd">reports</span>. {t("admin.reports.subtitle_ban_uses", { defaultValue: "Ban uses" })}{" "}
          <span className="admin1-users__kbd">blockUser</span>.
        </p>
      </header>

      <div className="admin1-users__toolbar admin1-reports__toolbar">
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("common.status", { defaultValue: "Status" })}</span>
          <select
            className="admin1-users__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t("common.all", { defaultValue: "All" })}</option>
            <option value="pending">{t("admin.status.pending", { defaultValue: "pending" })}</option>
            <option value="pending_admin_review">
              {t("admin.status.pending_admin_review", { defaultValue: "pending_admin_review" })}
            </option>
            <option value="open">{t("admin.status.open", { defaultValue: "open" })}</option>
            <option value="under_review">{t("admin.status.under_review", { defaultValue: "under_review" })}</option>
          </select>
        </label>
        <label className="admin1-users__field">
          <span className="admin1-users__label">{t("admin.reports.context", { defaultValue: "Context" })}</span>
          <select
            className="admin1-users__select"
            value={contextFilter}
            onChange={(e) => setContextFilter(e.target.value)}
          >
            <option value="all">{t("common.all", { defaultValue: "All" })}</option>
            <option value="call">{t("admin.reports.context_call", { defaultValue: "call" })}</option>
            <option value="message">{t("admin.reports.context_message", { defaultValue: "message" })}</option>
            <option value="profile">{t("admin.reports.context_profile", { defaultValue: "profile" })}</option>
            <option value="__empty__">{t("common.none_parenthesized", { defaultValue: "(none)" })}</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="admin1-state-card">Loading reports…</div>
      ) : filtered.length === 0 ? (
        <div className="admin1-state-card">
          {reports.length === 0
            ? "No active reports (pending, open, or under review)."
            : "No reports match the filters."}
        </div>
      ) : (
        <section className="admin1-reports__grid">
          {filtered.map((report) => {
            const id = report.id;
            const reportedUid = String(report.reportedUid || "").trim();
            const isWorking = busy && workingId === id;
            const feedback = feedbackById[id];
            const blockStatus = blockStatusById[id];
            const canBan =
              reportedUid && adminUid && reportedUid !== adminUid;
            const statusNow = normalize(report.status);

            return (
              <article key={id} className="admin1-reports__card">
                <div className="admin1-reports__card-head">
                  <div>
                    <div className="admin1-reports__card-title">
                      Report{" "}
                      <span className="admin1-vq__mono">{shorten(id)}</span>
                    </div>
                    <div className="admin1-vq__mono admin1-vq__mono--full">
                      {id}
                    </div>
                  </div>
                  <span className={badgeClass(report.status)}>
                    {String(report.status ?? "—")}
                  </span>
                </div>

                <dl className="admin1-vq__dl">
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.reporter_uid", { defaultValue: "reporterUid" })}</dt>
                    <dd className="admin1-reports__mono">{report.reporterUid}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.reported_uid", { defaultValue: "reportedUid" })}</dt>
                    <dd className="admin1-reports__mono">{report.reportedUid}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.reason", { defaultValue: "reason" })}</dt>
                    <dd>{String(report.reason ?? "—")}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.details", { defaultValue: "details" })}</dt>
                    <dd className="admin1-reports__wrap">
                      {report.details != null && report.details !== ""
                        ? String(report.details)
                        : "—"}
                    </dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.context_type", { defaultValue: "contextType" })}</dt>
                    <dd>{report.contextType ? String(report.contextType) : "—"}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.call_id", { defaultValue: "callId" })}</dt>
                    <dd className="admin1-reports__mono">
                      {report.callId ? String(report.callId) : "—"}
                    </dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.chat_id", { defaultValue: "chatId" })}</dt>
                    <dd className="admin1-reports__mono">
                      {report.chatId ? String(report.chatId) : "—"}
                    </dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.message_id", { defaultValue: "messageId" })}</dt>
                    <dd className="admin1-reports__mono">
                      {report.messageId ? String(report.messageId) : "—"}
                    </dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.reports.moderation_action_id", { defaultValue: "moderationActionId" })}</dt>
                    <dd className="admin1-reports__mono">
                      {report.moderationActionId ? String(report.moderationActionId) : "—"}
                    </dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.fields.created_at", { defaultValue: "createdAt" })}</dt>
                    <dd>{formatCreatedAt(report.createdAt)}</dd>
                  </div>
                </dl>

                <div className="admin1-reports__block-status">
                  <span>{t("admin.reports.block_status_label", { defaultValue: "Personal block:" })}</span>{" "}
                  {blockStatus === undefined && (
                    <button
                      type="button"
                      className="admin1-reports__btn admin1-reports__btn--link"
                      onClick={() => checkBlockStatus(report)}
                    >
                      {t("admin.reports.check_block_status", { defaultValue: "Check" })}
                    </button>
                  )}
                  {blockStatus === "loading" && "…"}
                  {blockStatus === "error" && (
                    <span className="admin1-vq__feedback">
                      {t("admin.reports.block_status_error", { defaultValue: "Could not check." })}
                    </span>
                  )}
                  {blockStatus && typeof blockStatus === "object" && (
                    <strong>
                      {blockStatus.reporterBlockedReported
                        ? t("admin.reports.block_status_active", { defaultValue: "active" })
                        : t("admin.reports.block_status_inactive", {
                            defaultValue: "not active - the reporter never blocked this user, or later unblocked them",
                          })}
                    </strong>
                  )}
                  <div className="admin1-reports__block-status-note">
                    {t("admin.reports.block_status_note", {
                      defaultValue:
                        "Admin review never unblocks or re-blocks automatically - this is read-only.",
                    })}
                  </div>
                </div>

                <div className="admin1-reports__actions">
                  <button
                    type="button"
                    className="admin1-reports__btn admin1-reports__btn--review"
                    disabled={busy || statusNow === "under_review"}
                    onClick={() => markUnderReview(report)}
                  >
                    {isWorking ? "…" : "Mark under review"}
                  </button>
                  <button
                    type="button"
                    className="admin1-reports__btn admin1-reports__btn--close"
                    disabled={busy}
                    onClick={() => closeReport(report)}
                  >
                    {isWorking ? "…" : "Close report"}
                  </button>
                  <Link
                    className="admin1-reports__btn admin1-reports__btn--link"
                    to={`/admin1/user/${encodeURIComponent(reportedUid)}`}
                  >
                    View reported user
                  </Link>
                  {canBan ? (
                    <button
                      type="button"
                      className="admin1-reports__btn admin1-reports__btn--ban"
                      disabled={busy}
                      onClick={() => banReportedUser(report)}
                    >
                      {isWorking ? "…" : "Ban reported user"}
                    </button>
                  ) : null}
                </div>

                {feedback ? (
                  <div className="admin1-vq__feedback">{feedback}</div>
                ) : null}
              </article>
            );
          })}
        </section>
      )}

      {!loading && filtered.length > 0 ? (
        <p className="admin1-users__meta">
          Showing {filtered.length} of {reports.length} loaded reports
        </p>
      ) : null}
    </div>
  );
}
